import { isSourcingActive, type SourcingRunStatus } from "./types/sourcing-run";

/** Whether the admin's Search restart can go ahead (#18). Mirrors the backend,
 * which refuses a restart while the job's search is running. */
export interface SearchRestartPlan {
  /** The latest search or the counts aren't current yet; don't offer the
   * restart until they are. */
  checking: boolean;
  blocked: boolean;
  /** The running search was already asked to stop (#88). */
  stopping: boolean;
}

/** `latestRunStatus` is `undefined` while loading, `null` when the job never
 * had a search. `refreshing`: cached data is shown while a fresh fetch runs. */
export function searchRestartPlan(
  latestRunStatus: SourcingRunStatus | null | undefined,
  {
    refreshing = false,
    stopRequested = false,
  }: { refreshing?: boolean; stopRequested?: boolean } = {},
): SearchRestartPlan {
  const blocked = latestRunStatus != null && isSourcingActive(latestRunStatus);
  return {
    checking: latestRunStatus === undefined || refreshing,
    blocked,
    stopping: blocked && stopRequested,
  };
}

/** What a restart deletes, by group — the backend's restart preview. */
export interface RestartPreview {
  longlist: number;
  rejected: number;
  pool: number;
}

function countOf(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** The sentence the restart dialog shows about what gets deleted. */
export function restartDeletionSummary({ longlist, rejected, pool }: RestartPreview): string {
  const parts = [
    longlist > 0 && countOf(longlist, "Longlist-Talent", "Longlist-Talente"),
    rejected > 0 &&
      countOf(
        rejected,
        "abgelehnter Kandidat (inkl. Ablehnungsgrund)",
        "abgelehnte Kandidaten (inkl. Ablehnungsgründe)",
      ),
    pool > 0 && countOf(pool, "aussortiertes Profil", "aussortierte Profile"),
  ].filter((part): part is string => Boolean(part));
  if (parts.length === 0) return "Es werden keine Talente gelöscht.";
  const listed =
    parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(", ")} und ${parts.at(-1)}`;
  const singular = parts.length === 1 && longlist + rejected + pool === 1;
  return `${listed} ${singular ? "wird" : "werden"} gelöscht.`;
}
